import CancelIcon from 'images/icons/Cancel.svg';
import { memo } from 'preact/compat';
import { useCallback, useRef } from 'preact/hooks';
import { useEffect } from 'react';
import { Response, useImageUpload } from 'utilities/api/use-image-upload';

import { Icon } from 'components/Icon';
import { Spinner } from 'components/Spinner';

import styles from '../../ImageUpload.module.scss';

interface UploadedItemProps {
  file: File;
  id: string;
  onRemove: (id: string) => void;
  onUpload: (request: Promise<Response | Error>) => void;
}

export const UploadedItem = memo(function UploadedItem({
  file,
  id,
  onRemove,
  onUpload,
}: UploadedItemProps) {
  const { upload, isUploading } = useImageUpload();
  const uploaded = useRef(false);

  const handleClick = useCallback(
    (event: Event) => {
      event.stopPropagation();
      onRemove(id);
    },
    [id, onRemove],
  );

  useEffect(() => {
    async function uploadImage() {
      uploaded.current = true;

      const request = upload(file);

      onUpload(request);
    }

    if (!uploaded.current) {
      uploadImage();
    }
  }, [id, file, upload, onUpload]);

  const loadingMarkup = isUploading ? (
    <div className={styles.LoadingOverlay}>
      <Spinner size="small" />
    </div>
  ) : null;

  return (
    <div className={styles.UploadedItem}>
      {loadingMarkup}
      <div className={styles.Image} onClick={handleClick}>
        <img src={URL.createObjectURL(file)} alt={file.name} />
        <div className={styles.ImageRemove}>
          <Icon source={CancelIcon} />
        </div>
      </div>
    </div>
  );
});
